import { HttpErrorResponse } from '@angular/common/http';

export const UNSAFE_INPUT_ERROR_CODE = 'InputSafety.UnsafeInput';

const PROBLEM_TYPE = 'https://tools.ietf.org/html/rfc7231#section-6.5.1';
const PROBLEM_TITLE = 'Validation failure';
const PROBLEM_STATUS = 400;
const PROBLEM_STATUS_TEXT = 'Bad Request';
const PROBLEM_DETAIL = 'One or more validation errors occurred.';
const QUERY_PROPERTY_NAME = 'query';
const UNSAFE_VALUE_MESSAGE = 'The value contains unsafe markup.';
const UNSAFE_KEY_MESSAGE = 'A query parameter name contains unsafe markup.';

/**
 * One entry of the rejection body's `errors` list: the offending parameter and why it was refused. An unsafe value
 * is named `query.<key>`; an unsafe key is named plain `query`, because the key itself must never be repeated.
 */
export interface UnsafeInputIssue {
  propertyName: string;
  errorMessage: string;
}

/**
 * The 400 body for a query the input-safety rule rejects.
 *
 * The first four members are what `ValidationEndpointFilter` sends for any validation failure, so the client
 * and Kt.Api answer with one shape. Kt.Api may add extensions such as `requestId`; they play no part here.
 */
export interface UnsafeInputProblem {
  type: typeof PROBLEM_TYPE;
  title: typeof PROBLEM_TITLE;
  status: typeof PROBLEM_STATUS;
  detail: typeof PROBLEM_DETAIL;
  errors: UnsafeInputIssue[];
  errorCode: typeof UNSAFE_INPUT_ERROR_CODE;
}

/**
 * Builds the rejection body. It never carries the unsafe value and has no `message` field, so the global
 * `errorInterceptor` looks up the generic `ERROR.400:GENERIC` translation. No toast shows while that key stays
 * untranslated; translating it would make every rejection toast.
 *
 * @param errors One entry per offending parameter, in order of first appearance in the query string.
 * @returns The rejection body.
 */
export function buildUnsafeInputProblem(errors: readonly UnsafeInputIssue[]): UnsafeInputProblem {
  return {
    type: PROBLEM_TYPE,
    title: PROBLEM_TITLE,
    status: PROBLEM_STATUS,
    detail: PROBLEM_DETAIL,
    errors: errors.map((error) => ({ ...error })),
    errorCode: UNSAFE_INPUT_ERROR_CODE,
  };
}

/**
 * Builds the failure the client raises for a request it refuses to send: the 400 an `HttpErrorResponse` carries,
 * with the rejection body.
 *
 * @param url The request URL; the rejection carries it cut off at its first `?` or `#`, so the term is never repeated.
 * @param errors One entry per offending parameter, in order of first appearance in the query string.
 * @returns The rejection to fail the request with.
 */
export function buildUnsafeInputRejection(url: string, errors: readonly UnsafeInputIssue[]): HttpErrorResponse {
  return new HttpErrorResponse({
    status: PROBLEM_STATUS,
    statusText: PROBLEM_STATUS_TEXT,
    url: withoutQueryOrFragment(url),
    error: buildUnsafeInputProblem(errors),
  });
}

/**
 * Tells whether a failure is the unsafe-input rejection, whether the client or Kt.Api produced it.
 *
 * Recognizes an `HttpErrorResponse` with the rejection body, the bare body the generated client throws with
 * `throwOnError`, and an `Error` whose `cause` is one of these (inspected once, never walked deeper). The status
 * and `title` play no part, so a rejection body behind another status is recognized too.
 *
 * @param error The rejection to inspect.
 * @returns Whether the failure is the unsafe-input rejection.
 */
export function isUnsafeInputRejection(error: unknown): boolean {
  if (carriesErrorCode(error)) {
    return true;
  }

  return error instanceof Error && carriesErrorCode(error.cause);
}

/**
 * Describes an unsafe query key. Every unsafe key shares this one entry, which never names the key.
 *
 * @returns The `errors` entry for an unsafe key.
 */
export function unsafeKeyIssue(): UnsafeInputIssue {
  return { propertyName: QUERY_PROPERTY_NAME, errorMessage: UNSAFE_KEY_MESSAGE };
}

/**
 * Describes an unsafe query value.
 *
 * @param key The query key as sent after the first decode, in the spelling of its first appearance.
 * @returns The `errors` entry for that parameter.
 */
export function unsafeValueIssue(key: string): UnsafeInputIssue {
  return { propertyName: `${QUERY_PROPERTY_NAME}.${key}`, errorMessage: UNSAFE_VALUE_MESSAGE };
}

/**
 * Reads the error code from an `HttpErrorResponse` body or from a bare body object.
 *
 * @param value The value to inspect.
 * @returns Whether it carries the unsafe-input error code.
 */
function carriesErrorCode(value: unknown): boolean {
  const body: unknown = value instanceof HttpErrorResponse ? value.error : value;

  return typeof body === 'object' && body !== null && 'errorCode' in body && body.errorCode === UNSAFE_INPUT_ERROR_CODE;
}

/**
 * Cuts the query and the fragment off a URL.
 *
 * @param url The request URL.
 * @returns The URL up to its first `?` or `#`.
 */
function withoutQueryOrFragment(url: string): string {
  return url.split(/[#?]/, 1)[0];
}
